import { PLACEHOLDER_STUDY_NAME, deriveStudyNameFromFilename } from '@suvoda-uat/domain';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './api-client.js';
import {
  type LocalMove,
  applyMoveLocally,
  bumpScriptVersion,
  clearInclusionLocally,
  clearNodeLocally,
  dropScriptVersion,
  excludeNodeLocally,
  excludeTestCaseLocally,
  includeNodeLocally,
  includeTestCaseLocally,
  markCompleteLocally,
  raiseScriptVersionTo,
  reopenLocally,
  replaceSectionInScript,
  replaceSubsectionInScript,
  replaceTestCaseInScript,
  setFunctionLocally,
  setRolesLocally,
  setTitleLocally,
} from './assembly.js';
import { generationPollInterval } from './generation.js';

/**
 * TanStack Query data layer (R11). Mutations invalidate the trial + pack
 * queries so the next If-Match uses the server's freshest version. The upload
 * helper performs the R5 two-step: presign → PUT bytes straight to S3 (with the
 * sha-256 as object metadata) → record metadata via the API.
 */
export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

export const useMasterTemplateVersions = () =>
  useQuery({ queryKey: ['master-templates'], queryFn: api.listMasterTemplateVersions });

/**
 * The Dashboard's minimal Trial list (T053) — the acting user's Trials.
 * F-018 (FR-009, R7): navigate-back and tab-refocus MUST refetch so the card's
 * "updated" date reflects new activity without a manual reload. The options are
 * EXPLICIT — today's TanStack defaults happen to refetch, but a future global
 * `staleTime` would silently break the guarantee; `'always'` decouples it. The
 * existing seven `['trials']` invalidations stay as they are (no per-mutation
 * fan-out — the on-display refetch covers the edit→dashboard journey).
 */
export const useTrials = () =>
  useQuery({
    queryKey: ['trials'],
    queryFn: api.listTrials,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

export const trialQueryOptions = (trialId: string) => ({
  queryKey: ['trial', trialId],
  queryFn: () => api.getTrial(trialId),
});

export const useTrial = (trialId: string) =>
  useQuery({ ...trialQueryOptions(trialId), enabled: trialId !== '' });

export const useTrialPack = (trialId: string) =>
  useQuery({
    queryKey: ['pack', trialId],
    queryFn: () => api.getTrialPack(trialId),
    enabled: trialId !== '',
  });

/** The generated Test Script for the UAT/Assembly view (US1) — keyed per trial. */
export const useTestScript = (trialId: string, options?: { readonly live?: boolean }) =>
  useQuery({
    queryKey: ['test-script', trialId],
    queryFn: () => api.listTestScript(trialId),
    // F-016 FR-024: the detail view polls so a companion-applied Edit
    // Proposal lands on-screen within <=10 s without a manual reload (the
    // refreshed version also keeps If-Match tokens current). TanStack
    // dedupes subscribers; the most aggressive interval wins.
    ...(options?.live === true
      ? { refetchInterval: 8_000, refetchOnWindowFocus: 'always' as const }
      : {}),
  });

/**
 * F-013 (R11): the render report for the trial's latest finalised version.
 * Disabled until a finalised version exists; keyed per (trial, version) so a
 * re-finalisation refetches its own immutable report.
 */
export const useRenderReport = (trialId: string, version: string | undefined) =>
  useQuery({
    queryKey: ['render-report', trialId, version],
    queryFn: () => api.getRenderReport(trialId, version as string),
    enabled: version !== undefined,
  });

/** The admin-only Team directory (feature 015). */
export const useTeamMembers = () =>
  useQuery({ queryKey: ['team-members'], queryFn: api.listTeamMembers });

/**
 * F-139 (R7/R9): the workspace assignable-owner list. Fetched ONCE under a shared
 * key and reused across every dashboard card (flat-team scale — no per-card
 * fetch). Reusable by #141's reviewer picker.
 */
export const useAssignableUsers = (options?: { readonly enabled?: boolean }) =>
  useQuery({
    queryKey: ['assignable-users'],
    queryFn: api.listAssignableUsers,
    // Editor-tier endpoint — skip the (doomed-403) fetch for viewers.
    enabled: options?.enabled ?? true,
  });

const sha256Hex = async (buffer: ArrayBuffer): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const uploadDocument = async (params: {
  trialId: string;
  slot: api.SlotName;
  file: File;
  version: number;
}): Promise<void> => {
  // Compute the sha-256 first so it can be signed into the presigned PUT (the
  // server signs it as object metadata; real S3 rejects an unsigned header).
  const contentHash = await sha256Hex(await params.file.arrayBuffer());
  const { url, storageUri } = await api.createUploadUrl(
    params.trialId,
    params.slot,
    params.file.type,
    contentHash,
  );
  const put = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': params.file.type, 'x-amz-meta-content-hash': contentHash },
    body: params.file,
  });
  if (!put.ok) throw new api.ApiError(put.status, 'Upload to storage failed.');
  await api.putSlot(
    params.trialId,
    params.slot,
    {
      filename: params.file.name,
      sizeBytes: params.file.size,
      mimeType: params.file.type,
      contentHash,
      storageUri,
    },
    params.version,
  );
};

/**
 * F-020 (R5): correct the study name from the dashboard card. Invalidates the
 * trials list (and the trial) so every display reflects the new value (FR-008).
 */
export const useRenameTrial = () => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ trialId, name }: { trialId: string; name: string }) =>
      api.renameTrial(trialId, name),
    onSuccess: (_trial, { trialId }) => {
      void queries.invalidateQueries({ queryKey: ['trials'] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/**
 * F-139 (R1/R9): reassign a Trial's Owner from the dashboard card. Invalidates
 * the trials list (and the trial) so every display — including "My Projects"
 * membership, which is a projection of `owner` — reflects the new owner
 * immediately (SC-001). Mirrors useRenameTrial.
 */
export const useSetTrialOwner = () => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ trialId, ownerId }: { trialId: string; ownerId: string }) =>
      api.setTrialOwner(trialId, ownerId),
    onSuccess: (_trial, { trialId }) => {
      void queries.invalidateQueries({ queryKey: ['trials'] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/**
 * F-033 (R6/FR-015): confirm a provisional system-derived name from the card.
 * Clears the cue + marks it user-set; invalidates the list (and the trial) so
 * the cue disappears everywhere. Mirrors useRenameTrial / the acknowledge ops.
 */
export const useConfirmTrialName = () => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ trialId }: { trialId: string }) => api.confirmTrialName(trialId),
    onSuccess: (_trial, { trialId }) => {
      void queries.invalidateQueries({ queryKey: ['trials'] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/**
 * F-020 (R1) stage-then-adopt: the FIRST upload of a not-yet-created project.
 * StageTrialDocument (trial-less presign) → S3 PUT → CreateTrial adopting the
 * staged object — so the Trial record exists IFF the upload succeeded (FR-002).
 * The name is derived from the filename (FR-003/FR-003a), falling back to the
 * fixed placeholder; the template version comes from the upload-screen picker.
 */
export const createTrialFromFirstUpload = async (params: {
  slot: api.SlotName;
  file: File;
  masterTemplateVersion: string;
}): Promise<api.Trial> => {
  const contentHash = await sha256Hex(await params.file.arrayBuffer());
  const { stagingKey, uploadUrl } = await api.stageTrialDocument({
    slot: params.slot,
    filename: params.file.name,
    sizeBytes: params.file.size,
    mimeType: params.file.type,
    contentHash,
  });
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': params.file.type, 'x-amz-meta-content-hash': contentHash },
    body: params.file,
  });
  if (!put.ok) throw new api.ApiError(put.status, 'Upload to storage failed.');
  return api.createTrial({
    name: deriveStudyNameFromFilename(params.file.name) ?? PLACEHOLDER_STUDY_NAME,
    masterTemplateVersion: params.masterTemplateVersion,
    firstDocument: {
      slot: params.slot,
      filename: params.file.name,
      sizeBytes: params.file.size,
      mimeType: params.file.type,
      contentHash,
      stagingKey,
    },
  });
};

/** Mutation wrapper for the first upload; refreshes the dashboard list. */
export const useCreateTrialFromFirstUpload = () => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: createTrialFromFirstUpload,
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

export const useUploadDocument = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['pack', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

export const useDeleteDocument = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (params: { slot: api.SlotName; version: number }) =>
      api.deleteSlot(trialId, params.slot, params.version),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['pack', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/**
 * Persist the Randomization Addendum toggle (F-021 US3). A versioned pack
 * mutation like upload/delete: on success the pack + trial queries refresh so
 * the next If-Match uses the server's freshest version.
 */
export const useSetRandomization = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (params: { enabled: boolean; version: number }) =>
      api.setRandomizationEnabled(trialId, params.enabled, params.version),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['pack', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/** Read a wire DocumentSlot's filename for display (null when the slot is empty). */
export const slotFilename = (slot: api.TrialPack['urs']): string | null => {
  if ('doc' in slot) return slot.doc.filename;
  if ('xlsx' in slot) return slot.xlsx.filename;
  return null;
};

/** Start or re-run generation for a Trial; refresh resume facts after the server accepts it. */
export const useStartGeneration = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: () => api.startGeneration(trialId),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['generation-job', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

/**
 * Poll the current generation job (R6/R11). `refetchInterval` returns 2s while
 * the job is in flight and false once terminal, so the poll stops itself.
 */
export const useCurrentGenerationJob = (trialId: string) =>
  useQuery({
    queryKey: ['generation-job', trialId],
    queryFn: () => api.getCurrentGenerationJob(trialId),
    refetchInterval: (query) => generationPollInterval(query.state.data),
  });

/**
 * Request a best-effort cancellation of the in-flight job (R2). On success it
 * refreshes the polled job so the Verification view reflects the requested
 * state; the poll keeps running until a terminal outcome arrives.
 */
export const useCancelGeneration = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.cancelGeneration(trialId, jobId),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['generation-job', trialId] });
    },
  });
};

/** The optimistic-concurrency target of a designer mutation (US2). */
export type TestCaseMutationArgs = { testCaseId: string; version: number };

export type ImportTestCaseArgs = TestCaseMutationArgs & { readonly sourceTestCaseId: string };
/** Editing content carries a full ordered row replacement request (FR-020). */
export type EditContentArgs = TestCaseMutationArgs & {
  readonly content: api.EditContentInput;
  readonly idempotencyKey?: string;
};
export type UpdateTitleArgs = TestCaseMutationArgs & {
  readonly title?: string;
  readonly reason?: string;
  readonly idempotencyKey?: string;
};
/** Setting a Test Case's Function — omit `function` to clear it (F-033 FR-008/010). */
export type UpdateFunctionArgs = TestCaseMutationArgs & {
  readonly function?: string;
  readonly reason?: string;
  readonly idempotencyKey?: string;
};
/** Setting a Test Case's study roles — a full-list replace (F-010 FR-012). */
export type SetRolesArgs = TestCaseMutationArgs & {
  readonly roles: readonly string[];
  readonly reason?: string;
  readonly idempotencyKey?: string;
};
export type CreateTestCaseArgs = {
  readonly subsectionId: string;
  readonly idempotencyKey: string;
};
/** Suggesting rows for a novel case takes only its current title (US4). */
export type SuggestRowsArgs = { readonly testCaseId: string; readonly title: string };
/** F-017 (US3): confirm a flagged variant default — body-versioned, no If-Match. */
export type ConfirmDecisionArgs = { readonly testCaseId: string; readonly expectedVersion: number };
/** F-017 (US3): override a variant applicability decision (reason required). */
export type OverrideDecisionArgs = ConfirmDecisionArgs & {
  readonly applicable: boolean;
  readonly reason: string;
};
export type FinaliseTrialArgs = { readonly version: string };

/** onMutate context: the snapshot of ONLY the target case, for single-case rollback. */
type CaseMutationContext = { readonly previousCase: api.TestCase | undefined };

/**
 * F-029 (US1, R1): the per-case mutation factory. Each per-case op already
 * RETURNS the updated `TestCase` (byte-identical to its slot in ListTestScript),
 * so the cache reconciles from the response instead of refetching the whole
 * 354-case script. Single-case granularity throughout (Codex MAJOR): snapshot +
 * roll back ONLY the target case, operating on the CURRENT cache each time —
 * never a whole-script snapshot — so two overlapping per-case mutations on
 * different cases stay independent (a failed one never wipes a concurrent
 * committed one).
 *
 *   onMutate  — cancel in-flight script reads; snapshot the target case; when a
 *               `predict` updater is supplied (the six instant actions) patch
 *               that one case optimistically (state + version+1). Edit-content
 *               omits `predict` → no optimistic patch (its rows lack the
 *               server-derived origin/order, R3); it still snapshots for rollback.
 *   onSuccess — splice the SERVER-returned case (authoritative version + derived
 *               fields). NO success-path test-script invalidate (no full refetch).
 *   onError   — restore ONLY the target case to its snapshot; on a 409 conflict
 *               invalidate once to reconcile true state (R5). Other failures
 *               leave the rolled-back cache as-is.
 *   opts.alsoInvalidate — extra keys to invalidate on success (set-roles
 *               regresses a finalised trial → ['trial'],['trials'], R8).
 */
const useOptimisticTestCaseMutation = <Args extends { readonly testCaseId: string }>(
  trialId: string,
  mutationFn: (args: Args) => Promise<api.TestCase>,
  predict?: (args: Args) => (testCase: api.TestCase) => api.TestCase,
  opts?: { readonly alsoInvalidate?: ReadonlyArray<ReadonlyArray<unknown>> },
) => {
  const queries = useQueryClient();
  const key = ['test-script', trialId];
  const findCase = (id: string): api.TestCase | undefined =>
    queries
      .getQueryData<api.TestScript>(key)
      ?.sections.flatMap((section) => section.subsections)
      .flatMap((subsection) => subsection.testCases)
      .find((testCase) => testCase.id === id);
  const splice = (id: string, update: (testCase: api.TestCase) => api.TestCase): void => {
    queries.setQueryData<api.TestScript>(key, (current) =>
      current === undefined ? current : replaceTestCaseInScript(current, id, update),
    );
  };
  return useMutation<api.TestCase, unknown, Args, CaseMutationContext>({
    mutationFn,
    onMutate: async (args) => {
      await queries.cancelQueries({ queryKey: key });
      const previousCase = findCase(args.testCaseId);
      if (predict !== undefined && previousCase !== undefined) {
        splice(args.testCaseId, predict(args));
      }
      return { previousCase };
    },
    onSuccess: (updated) => {
      splice(updated.id, () => updated);
      for (const extraKey of opts?.alsoInvalidate ?? []) {
        void queries.invalidateQueries({ queryKey: extraKey });
      }
    },
    onError: (error, _args, context) => {
      const previousCase = context?.previousCase;
      if (previousCase !== undefined) {
        splice(previousCase.id, () => previousCase);
      }
      // The 409 body carries only `currentVersion`, not the case's current
      // state — a single whole-script refetch is the only zero-contract
      // reconcile (R5). Other failures leave the rolled-back cache as-is.
      if (error instanceof api.ApiError && error.status === 409) {
        void queries.invalidateQueries({ queryKey: key });
      }
    },
  });
};

/**
 * Per-case review-action hooks (US2 write + F-029 US1 optimism). Rebuilt on
 * `useOptimisticTestCaseMutation`: the six instant actions predict the change in
 * `onMutate` and splice the server response on success (no whole-script
 * refetch). `version` rides as If-Match; the route owns the error surface.
 */
export const useIncludeTestCase = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, version }: TestCaseMutationArgs) =>
      api.includeTestCase(trialId, testCaseId, version),
    () => includeTestCaseLocally,
  );

export const useExcludeTestCase = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, version }: TestCaseMutationArgs) =>
      api.excludeTestCase(trialId, testCaseId, version),
    () => excludeTestCaseLocally,
  );

/** Clear a Test Case's Inclusion Decision back to `undecided` (FR-013) — the deselect toggle. */
export const useClearInclusion = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, version }: TestCaseMutationArgs) =>
      api.clearInclusion(trialId, testCaseId, version),
    () => clearInclusionLocally,
  );

// ── Feature 145: optimistic all-or-nothing subsection exclude cascade ────────
//
// Excluding a whole NON-instructional subsection (distinct from the F-133
// instructional-node `useExcludeSubsection` above) cascades the EXISTING per-case
// `excludeTestCase` over the subsection's canonical children. There is no bulk
// operation (zero-contract by design), so all-or-nothing FROM THE DESIGNER'S
// PERSPECTIVE (FR-010) is delivered by: one optimistic patch (every child →
// excluded, rollup all_excluded) → `Promise.allSettled` of independent per-case
// writes (each carries its OWN version, so no shared-aggregate prediction) → on
// all-fulfilled splice each server case (no whole-script refetch, SC-007). The
// any-failure compensation + revert + reconcile-to-truth is the US4 hardening
// (added below in the failure branch).

type SettledTestCase = PromiseSettledResult<api.TestCase>;
const isFulfilledCase = (result: SettledTestCase): result is PromiseFulfilledResult<api.TestCase> =>
  result.status === 'fulfilled';
const isRejectedCase = (result: SettledTestCase): result is PromiseRejectedResult =>
  result.status === 'rejected';

/** F-145 (US1): the canonical children a subsection-exclude cascade targets. */
export type ExcludeSubsectionArgs = { readonly cases: readonly api.TestCase[] };

/** onMutate context: the pre-action snapshot of every target case, for rollback. */
type CascadeContext = { readonly snapshot: readonly api.TestCase[] };

/**
 * F-145 (US1 apply + US4 failure): the subsection-exclude cascade hook. `mutate`
 * takes the subsection's canonical `cases` (the unfiltered children, FR-003); the
 * input cases ARE the snapshot (the current cache slot, each carrying its own
 * version + prior decision).
 *
 *   onMutate  — cancel in-flight script reads; one optimistic patch excludes every
 *               target (`excludeTestCaseLocally`: state→excluded, version+1),
 *               recomputing the owning subsection's rollup to `all_excluded` once,
 *               so the cost is independent of subsection size (SC-007/I-SI-10).
 *   mutationFn — fire all `excludeTestCase(id, snapshotVersion)` via
 *               `Promise.allSettled` (each case its OWN per-case version — no
 *               shared-aggregate prediction). On all-fulfilled resolve the server
 *               cases. On ANY rejection (US4/I-SI-09) COMPENSATE the FULFILLED
 *               subset back to its prior decision — using the fulfilled response's
 *               RETURNED version (the case advanced a step server-side; the snapshot
 *               version would 409) — then throw so `onError` reconciles.
 *   onSuccess — splice each authoritative server case; NO `['test-script']` refetch.
 *   onError   — revert the optimistic patch to the snapshot and invalidate
 *               `['test-script']` once (reconcile-to-truth even if a compensation
 *               also failed), so the subsection is never displayed half-excluded.
 */
export const useExcludeSubsectionCascade = (trialId: string) => {
  const queries = useQueryClient();
  const key = ['test-script', trialId];
  const patch = (update: (script: api.TestScript) => api.TestScript): void => {
    queries.setQueryData<api.TestScript>(key, (current) =>
      current === undefined ? current : update(current),
    );
  };
  // Return ONE succeeded case to its prior decision via the existing per-case op,
  // using the version the exclude RETURNED (not the snapshot) so the compensating
  // write isn't itself a stale 409. A prior 'excluded' case is already where we
  // want it → no-op.
  const compensate = (prior: api.TestCase, returnedVersion: number): Promise<unknown> => {
    if (prior.inclusionDecision.state === 'included') {
      return api.includeTestCase(trialId, prior.id, returnedVersion);
    }
    if (prior.inclusionDecision.state === 'undecided') {
      return api.clearInclusion(trialId, prior.id, returnedVersion);
    }
    return Promise.resolve();
  };
  return useMutation<readonly api.TestCase[], unknown, ExcludeSubsectionArgs, CascadeContext>({
    mutationFn: async ({ cases }) => {
      const results = await Promise.allSettled(
        cases.map((testCase) => api.excludeTestCase(trialId, testCase.id, testCase.version)),
      );
      if (results.some(isRejectedCase)) {
        // All-or-nothing: undo the writes that DID land (pairing each prior case
        // with its own settled result by index), then surface the failure.
        const fulfilled = cases.flatMap((prior, index) => {
          const result = results[index];
          return result !== undefined && isFulfilledCase(result)
            ? [{ prior, returnedVersion: result.value.version }]
            : [];
        });
        await Promise.allSettled(
          fulfilled.map(({ prior, returnedVersion }) => compensate(prior, returnedVersion)),
        );
        const rejection = results.find(isRejectedCase);
        throw rejection !== undefined ? rejection.reason : new Error('Exclude cascade failed.');
      }
      return results.flatMap((result) => (isFulfilledCase(result) ? [result.value] : []));
    },
    onMutate: async ({ cases }) => {
      await queries.cancelQueries({ queryKey: key });
      patch((script) =>
        cases.reduce(
          (acc, testCase) => replaceTestCaseInScript(acc, testCase.id, excludeTestCaseLocally),
          script,
        ),
      );
      return { snapshot: cases };
    },
    onSuccess: (serverCases) => {
      patch((script) =>
        serverCases.reduce(
          (acc, serverCase) => replaceTestCaseInScript(acc, serverCase.id, () => serverCase),
          script,
        ),
      );
    },
    onError: (_error, _args, context) => {
      if (context?.snapshot !== undefined) {
        patch((script) =>
          context.snapshot.reduce(
            (acc, prior) => replaceTestCaseInScript(acc, prior.id, () => prior),
            script,
          ),
        );
      }
      // Reconcile to server truth even if a compensation also failed — the UI is
      // never left displaying a half-excluded subsection (FR-010/SC-005/I-SI-09).
      void queries.invalidateQueries({ queryKey: key });
    },
  });
};

/**
 * F-017 (US3): confirm a flagged variant default in one click (SC-013). On
 * success the script (and the trial — an override can regress a finalised one)
 * are invalidated so the projected state + version refresh.
 */
export const useConfirmOccurrenceDecision = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ testCaseId, expectedVersion }: ConfirmDecisionArgs) =>
      api.confirmOccurrenceDecision(trialId, testCaseId, expectedVersion),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

/** F-017 (US3): override a variant applicability decision (reason required, FR-009). */
export const useOverrideOccurrenceDecision = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ testCaseId, applicable, reason, expectedVersion }: OverrideDecisionArgs) =>
      api.overrideOccurrenceDecision(trialId, testCaseId, { applicable, reason }, expectedVersion),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

export const useMarkComplete = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, version }: TestCaseMutationArgs) =>
      api.markComplete(trialId, testCaseId, version),
    () => markCompleteLocally,
  );

/** Reopen (deselect "Mark as Complete") a Test Case (F-038 US2/FR-005) — the
 * optimistic inverse of useMarkComplete; returns the case to incomplete. */
export const useReopen = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, version }: TestCaseMutationArgs) =>
      api.reopenTestCase(trialId, testCaseId, version),
    () => reopenLocally,
  );

/**
 * Edit a Test Case's content (US3, FR-020). PATCHes the ordered task rows with
 * the resource `version` (If-Match) and, on success, invalidates
 * the Trial's `['test-script', trialId]` query so the next read reflects the
 * edit + bumped version.
 */
export const useCreateTestCase = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ subsectionId, idempotencyKey }: CreateTestCaseArgs) =>
      api.createTestCase(trialId, subsectionId, idempotencyKey),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

export type CreateSectionArgs = {
  title: string;
  afterSectionId?: string;
  idempotencyKey: string;
};

export type CreateSubsectionArgs = {
  sectionId: string;
  title: string;
  afterSubsectionId?: string;
  idempotencyKey: string;
};

/** Insert a novel Section (F-011); on success refetch the script + trial counters. */
export const useCreateSection = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ title, afterSectionId, idempotencyKey }: CreateSectionArgs) =>
      api.createSection(
        trialId,
        { title, ...(afterSectionId === undefined ? {} : { afterSectionId }) },
        idempotencyKey,
      ),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

/** Insert a novel Subsection into a Section (F-011). */
export const useCreateSubsection = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, title, afterSubsectionId, idempotencyKey }: CreateSubsectionArgs) =>
      api.createSubsection(
        trialId,
        sectionId,
        { title, ...(afterSubsectionId === undefined ? {} : { afterSubsectionId }) },
        idempotencyKey,
      ),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

/**
 * Feature 015 moves — the repo's first OPTIMISTIC mutations. A drag must not
 * snap back while the refetch is in flight, so `onMutate` applies the move to
 * the cached tree exactly as the server will (applyMoveLocally), `onError`
 * rolls back to the snapshot, and `onSettled` refetches to reconcile (orders
 * match by construction, so the reconcile is invisible). The trial query is
 * also refreshed — a move regresses a finalised Trial.
 */
const useOptimisticMove = <Args>(
  trialId: string,
  mutationFn: (args: Args) => Promise<unknown>,
  toLocalMove: (args: Args) => LocalMove,
) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async (args: Args) => {
      await queries.cancelQueries({ queryKey: ['test-script', trialId] });
      const previous = queries.getQueryData<api.TestScript>(['test-script', trialId]);
      if (previous !== undefined) {
        queries.setQueryData(
          ['test-script', trialId],
          applyMoveLocally(previous, toLocalMove(args)),
        );
      }
      return { previous };
    },
    onError: (_error, _args, context) => {
      if (context?.previous !== undefined) {
        queries.setQueryData(['test-script', trialId], context.previous);
      }
    },
    onSettled: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
    },
  });
};

export type MoveSectionArgs = {
  sectionId: string;
  afterSectionId: string | null;
  idempotencyKey: string;
};

export const useMoveSection = (trialId: string) =>
  useOptimisticMove(
    trialId,
    ({ sectionId, afterSectionId, idempotencyKey }: MoveSectionArgs) =>
      api.moveSection(
        trialId,
        sectionId,
        afterSectionId === null ? {} : { afterSectionId },
        idempotencyKey,
      ),
    ({ sectionId, afterSectionId }) => ({ kind: 'section', sectionId, afterSectionId }),
  );

export type MoveSubsectionArgs = {
  subsectionId: string;
  afterSubsectionId: string | null;
  idempotencyKey: string;
};

export const useMoveSubsection = (trialId: string) =>
  useOptimisticMove(
    trialId,
    ({ subsectionId, afterSubsectionId, idempotencyKey }: MoveSubsectionArgs) =>
      api.moveSubsection(
        trialId,
        subsectionId,
        afterSubsectionId === null ? {} : { afterSubsectionId },
        idempotencyKey,
      ),
    ({ subsectionId, afterSubsectionId }) => ({
      kind: 'subsection',
      subsectionId,
      afterSubsectionId,
    }),
  );

export type MoveTestCaseArgs = {
  testCaseId: string;
  toSubsectionId: string;
  afterTestCaseId: string | null;
  idempotencyKey: string;
};

export const useMoveTestCase = (trialId: string) =>
  useOptimisticMove(
    trialId,
    ({ testCaseId, toSubsectionId, afterTestCaseId, idempotencyKey }: MoveTestCaseArgs) =>
      api.moveTestCase(
        trialId,
        testCaseId,
        { toSubsectionId, ...(afterTestCaseId === null ? {} : { afterTestCaseId }) },
        idempotencyKey,
      ),
    ({ testCaseId, toSubsectionId, afterTestCaseId }) => ({
      kind: 'testCase',
      testCaseId,
      toSubsectionId,
      afterTestCaseId,
    }),
  );

/**
 * Edit a Test Case's content (US3, FR-020). F-029 (R3): NO optimistic `predict`
 * — the submitted rows carry `body`/`kind` but lack the server-derived `origin`
 * (template/edited/novel) and `order`, so an optimistic row would show a wrong
 * provenance chip; instead it splices the authoritative case on success
 * (dropping the whole-script refetch — the main win). The edit happens in the
 * edit view, so a brief wait for the now-fast round-trip is acceptable.
 */
export const useEditTestCaseContent = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, content, version, idempotencyKey }: EditContentArgs) =>
      api.editContent(trialId, testCaseId, content, version, idempotencyKey),
  );

/**
 * Import an existing case's content rows into the target (F-031, UAT-27). Like
 * the edit, there is NO optimistic `predict` — the appended rows are computed
 * server-side (fresh ids, derived origin), so it splices the authoritative case
 * on success (the rows appear without a reload, R11/I-IMP-09); a 409 refetches.
 */
export const useImportTestCase = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, sourceTestCaseId, version }: ImportTestCaseArgs) =>
      api.importTestCase(trialId, testCaseId, sourceTestCaseId, version),
  );

/**
 * Suggest draft rows for a novel case (US4). Draft-only: the result feeds the
 * LOCAL editor draft state and changes nothing server-side, so there is NO cache
 * invalidation (unlike the audited content save).
 */
export const useSuggestTestCaseRows = (trialId: string) =>
  useMutation({
    mutationFn: ({ testCaseId, title }: SuggestRowsArgs) =>
      api.suggestTestCaseRows(trialId, testCaseId, { title }),
  });

/** Args for the F-141 CreateReview mutation. */
export type CreateReviewArgs = {
  readonly trialId: string;
  readonly testCaseId: string;
  readonly body: api.CreateReviewBody;
  readonly idempotencyKey?: string;
};

/**
 * F-141 (#141): the reviews on a Test Case, newest-first. Viewer-tier — visible
 * to everyone who can view the trial (owner + viewers + reviewers); only the
 * create control is gated. Keyed by trial + case; disabled until both ids exist.
 */
export const useReviews = (trialId: string, testCaseId: string) =>
  useQuery({
    queryKey: ['reviews', trialId, testCaseId],
    queryFn: () => api.listReviews(trialId, testCaseId),
    enabled: trialId !== '' && testCaseId !== '',
  });

/**
 * F-141 (#141): attach a Review (comment/flag) to a Test Case. On success it
 * invalidates that case's reviews list so the new review appears (the create
 * control is shown only to a non-owner editor; the server is the real gate).
 */
export const useCreateReview = () => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ trialId, testCaseId, body, idempotencyKey }: CreateReviewArgs) =>
      api.createReview(trialId, testCaseId, body, idempotencyKey),
    onSuccess: (_review, { trialId, testCaseId }) => {
      void queries.invalidateQueries({ queryKey: ['reviews', trialId, testCaseId] });
    },
  });
};

/** Args for the F-013 node-decision hooks (aggregate version = If-Match). */
export type SectionDecisionArgs = { readonly sectionId: string; readonly version: number };
export type SubsectionDecisionArgs = { readonly subsectionId: string; readonly version: number };

/**
 * F-133: a node carrying an Instructional-Section Decision (Section | Subsection).
 * `find` locates it in the cached script; `replace` immutably swaps it (NO rollup
 * recompute — a decision target is always a zero-case node, R2).
 */
type NodeOps<Node> = {
  readonly find: (script: api.TestScript, nodeId: string) => Node | undefined;
  readonly replace: (
    script: api.TestScript,
    nodeId: string,
    update: (node: Node) => Node,
  ) => api.TestScript;
};

const sectionOps: NodeOps<api.Section> = {
  find: (script, id) => script.sections.find((section) => section.id === id),
  replace: replaceSectionInScript,
};

const subsectionOps: NodeOps<api.Subsection> = {
  find: (script, id) =>
    script.sections.flatMap((section) => section.subsections).find((sub) => sub.id === id),
  replace: replaceSubsectionInScript,
};

/** onMutate context: the target-node snapshot + the predicted aggregate version. */
type NodeDecisionContext<Node> = {
  readonly previousNode: Node | undefined;
  readonly predictedVersion: number;
};

/**
 * F-133: the single-node-granular optimistic factory for the six Instructional-
 * Section decisions — the node analogue of `useOptimisticTestCaseMutation`.
 * UNLIKE the per-case factory, these are versioned by the SHARED TSCRIPT META
 * aggregate version (`script.version`), and the node response omits it, so the
 * aggregate version is predicted and maintained with RELATIVE steps — never a
 * snapshot-restore, which would clobber a concurrent node decision or the
 * non-optimistic `updateSubsectionInstructions` (research R3, I-INO-04):
 *
 *   onMutate  — cancel in-flight script reads; snapshot ONLY the target node and
 *               the predicted version (`previous + 1`); patch that node's decision
 *               and apply a relative `+1` to `script.version` (I-INO-01/04).
 *   onSuccess — splice the SERVER-returned node and set `script.version =
 *               max(cached, predicted)` (the floor defends a racing 409-refetch,
 *               I-INO-04b); NO `['test-script']` refetch; invalidate `['trial']` +
 *               `['trials']` for the finalised-trial regress (I-INO-08, R6).
 *   onError   — restore ONLY the target node and apply a relative `-1`; on a 409,
 *               invalidate `['test-script']` once to reconcile (R5). Decisions on
 *               different nodes stay independent (single-node granularity, I-INO-06).
 */
const useOptimisticNodeDecisionMutation = <
  Args extends { readonly version: number },
  Node extends {
    readonly id: string;
    readonly inclusionDecision?: api.Section['inclusionDecision'];
  },
>(
  trialId: string,
  mutationFn: (args: Args) => Promise<Node>,
  nodeIdOf: (args: Args) => string,
  predict: (node: Node) => Node,
  ops: NodeOps<Node>,
) => {
  const queries = useQueryClient();
  const key = ['test-script', trialId];
  return useMutation<Node, unknown, Args, NodeDecisionContext<Node>>({
    mutationFn,
    onMutate: async (args) => {
      await queries.cancelQueries({ queryKey: key });
      const current = queries.getQueryData<api.TestScript>(key);
      const nodeId = nodeIdOf(args);
      const previousNode = current ? ops.find(current, nodeId) : undefined;
      const predictedVersion = (current?.version ?? args.version) + 1;
      if (current !== undefined && previousNode !== undefined) {
        queries.setQueryData<api.TestScript>(key, (cur) =>
          cur === undefined ? cur : bumpScriptVersion(ops.replace(cur, nodeId, predict)),
        );
      }
      return { previousNode, predictedVersion };
    },
    onSuccess: (node, _args, context) => {
      queries.setQueryData<api.TestScript>(key, (cur) =>
        cur === undefined
          ? cur
          : raiseScriptVersionTo(
              ops.replace(cur, node.id, () => node),
              context?.predictedVersion ?? cur.version,
            ),
      );
      // The decision can regress a finalised trial (FinaliseControl reads the
      // Trial), so refresh the trial + dashboard — but NEVER the whole script
      // (the splice above already reconciled the node), R6/I-INO-08.
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
    onError: (error, _args, context) => {
      const previousNode = context?.previousNode;
      if (previousNode !== undefined) {
        queries.setQueryData<api.TestScript>(key, (cur) =>
          cur === undefined
            ? cur
            : dropScriptVersion(ops.replace(cur, previousNode.id, () => previousNode)),
        );
      }
      // The 409 body carries only the current version, not the node's true
      // state — one whole-script refetch is the zero-contract reconcile (R5).
      if (error instanceof api.ApiError && error.status === 409) {
        void queries.invalidateQueries({ queryKey: key });
      }
    },
  });
};

export const useIncludeSection = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ sectionId, version }: SectionDecisionArgs) =>
      api.includeSection(trialId, sectionId, version),
    ({ sectionId }) => sectionId,
    includeNodeLocally,
    sectionOps,
  );

export const useExcludeSection = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ sectionId, version }: SectionDecisionArgs) =>
      api.excludeSection(trialId, sectionId, version),
    ({ sectionId }) => sectionId,
    excludeNodeLocally,
    sectionOps,
  );

export const useClearSectionInclusion = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ sectionId, version }: SectionDecisionArgs) =>
      api.clearSectionInclusion(trialId, sectionId, version),
    ({ sectionId }) => sectionId,
    clearNodeLocally,
    sectionOps,
  );

export const useIncludeSubsection = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ subsectionId, version }: SubsectionDecisionArgs) =>
      api.includeSubsection(trialId, subsectionId, version),
    ({ subsectionId }) => subsectionId,
    includeNodeLocally,
    subsectionOps,
  );

export const useExcludeSubsection = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ subsectionId, version }: SubsectionDecisionArgs) =>
      api.excludeSubsection(trialId, subsectionId, version),
    ({ subsectionId }) => subsectionId,
    excludeNodeLocally,
    subsectionOps,
  );

export const useClearSubsectionInclusion = (trialId: string) =>
  useOptimisticNodeDecisionMutation(
    trialId,
    ({ subsectionId, version }: SubsectionDecisionArgs) =>
      api.clearSubsectionInclusion(trialId, subsectionId, version),
    ({ subsectionId }) => subsectionId,
    clearNodeLocally,
    subsectionOps,
  );

export type UpdateInstructionsArgs = {
  readonly subsectionId: string;
  readonly body: string;
  readonly version: number;
  readonly reason?: string;
};

/**
 * F-113 (US3): edit a subsection's General Instructions. If-Match carries the
 * AGGREGATE (TestScriptContent) version; on success re-fetch the script (the card
 * re-renders with the new body, template highlights dropped) + the trial (the edit
 * bumps last-modified). Lifecycle-neutral — never regresses a finalised trial.
 */
export const useUpdateSubsectionInstructions = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ subsectionId, body, version, reason }: UpdateInstructionsArgs) =>
      api.updateSubsectionInstructions(trialId, subsectionId, body, version, reason),
    // Non-optimistic (the GI body has no cheap optimistic shape): re-fetch the
    // script (card re-renders, highlights dropped) + the trial/dashboard. This
    // also reconciles the SHARED aggregate `script.version` to truth — the seam
    // through which it interacts with the optimistic node decisions (F-133 R3).
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

export const useUpdateTestCaseTitle = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, title, reason, version, idempotencyKey }: UpdateTitleArgs) =>
      api.updateTitle(
        trialId,
        testCaseId,
        {
          ...(title === undefined ? {} : { title }),
          ...(reason === undefined ? {} : { reason }),
        },
        version,
        idempotencyKey,
      ),
    ({ title }) => setTitleLocally(title),
  );

/**
 * Set, change, or clear a Test Case's Function (F-033 FR-008). Like the roles
 * edit (and UNLIKE the title edit), the Function renders into the export and can
 * regress a finalised trial (FR-013), so it ALSO invalidates the Trial + the
 * dashboard list via `alsoInvalidate` — otherwise FinaliseControl (which reads
 * the Trial) would not reflect the regress. The script itself is reconciled by
 * the success splice (F-029), NOT a whole-script refetch. Omitting `function`
 * clears it (the optimistic splice drops the property; the server omits it too).
 */
export const useUpdateTestCaseFunction = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, function: fn, reason, version, idempotencyKey }: UpdateFunctionArgs) =>
      api.updateFunction(
        trialId,
        testCaseId,
        {
          ...(fn === undefined ? {} : { function: fn }),
          ...(reason === undefined ? {} : { reason }),
        },
        version,
        idempotencyKey,
      ),
    ({ function: fn }) => setFunctionLocally(fn),
    { alsoInvalidate: [['trial', trialId], ['trials']] },
  );

/**
 * Set a Test Case's study roles (F-010 FR-012). Unlike the other instant
 * actions, a role edit renders into the export and can regress a finalised trial
 * (FR-020), so it ALSO invalidates the Trial + the dashboard list via
 * `alsoInvalidate` — otherwise FinaliseControl (which reads the Trial) would not
 * reflect the regress (R8). The script itself is reconciled by the success
 * splice (F-029), NOT a whole-script refetch.
 */
export const useSetTestCaseRoles = (trialId: string) =>
  useOptimisticTestCaseMutation(
    trialId,
    ({ testCaseId, roles, reason, version, idempotencyKey }: SetRolesArgs) =>
      api.setRoles(
        trialId,
        testCaseId,
        { roles: [...roles], ...(reason === undefined ? {} : { reason }) },
        version,
        idempotencyKey,
      ),
    ({ roles }) => setRolesLocally(roles),
    { alsoInvalidate: [['trial', trialId], ['trials']] },
  );

export const useFinaliseTrial = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ version }: FinaliseTrialArgs) => api.finaliseTrial(trialId, version),
    onSettled: () => {
      void queries.invalidateQueries({ queryKey: ['trial', trialId] });
      void queries.invalidateQueries({ queryKey: ['trials'] });
    },
  });
};

/**
 * Run the FR-013 export/download capability. A plain mutation — there is no
 * cache key to invalidate; the caller branches on the returned `{ result }`
 * union (available → fetch the presigned DOCX; notAvailable → render the gate).
 */
export const usePrepareDownload = (trialId: string) =>
  useMutation({ mutationFn: () => api.prepareDownload(trialId) });

/**
 * F-149 (US1): run PrepareInterimDownload as a mutation (no cache key — each
 * request renders fresh, FR-012) and surface the `{ result }` union to the
 * interim-download flow. Idempotency-Key omitted (latent), like usePrepareDownload.
 */
export const usePrepareInterimDownload = (trialId: string) =>
  useMutation({ mutationFn: () => api.prepareInterimDownload(trialId) });

/** F-016 US4: related URS/TRS sections for a Test Case — polls every 5 s
 *  while the lazily-built source index reports `preparing` (SC-007). */
export const useRelatedSections = (
  trialId: string,
  testCaseId: string,
  options?: { readonly enabled?: boolean },
) =>
  useQuery({
    queryKey: ['related-sections', trialId, testCaseId],
    queryFn: () => api.listRelatedSections(trialId, testCaseId),
    refetchInterval: (query) => (query.state.data?.state === 'preparing' ? 5_000 : false),
    // F-016 ship-dark: parked bundles never fetch (the route is absent too).
    enabled: options?.enabled !== false,
  });

/** F-016 US4: the reading modal's full-section fetch (enabled on open). */
export const useSourceSection = (trialId: string, sectionId: string | undefined) =>
  useQuery({
    queryKey: ['source-section', trialId, sectionId],
    queryFn: () => api.getSourceSection(trialId, sectionId ?? ''),
    enabled: sectionId !== undefined,
  });

/**
 * F-017 (US2) — the Authoring Queue hooks. The list is viewer-readable; the
 * four transitions invalidate the queue so the next read reflects the new
 * status, and AUTHOR also invalidates the Test Script (a novel case landed in
 * the assembly). Each transition carries the item's `expectedVersion` — the
 * server rejects a stale write with 409.
 */
export const useAuthoringQueue = (trialId: string) =>
  useQuery({
    queryKey: ['authoring-queue', trialId],
    queryFn: () => api.listAuthoringQueue(trialId),
  });

type QueueItemArgs = { readonly itemId: string; readonly expectedVersion: number };

export const useStartAuthoringDraft = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, expectedVersion }: QueueItemArgs) =>
      api.startAuthoringDraft(trialId, itemId, expectedVersion),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['authoring-queue', trialId] });
    },
  });
};

export const useAuthorQueueItem = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      input,
    }: {
      readonly itemId: string;
      readonly input: api.AuthorQueueItemInput;
    }) => api.authorQueueItem(trialId, itemId, input),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['authoring-queue', trialId] });
      void queries.invalidateQueries({ queryKey: ['test-script', trialId] });
    },
  });
};

export const useDismissQueueItem = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      reason,
      expectedVersion,
    }: QueueItemArgs & { readonly reason: string }) =>
      api.dismissQueueItem(trialId, itemId, reason, expectedVersion),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['authoring-queue', trialId] });
    },
  });
};

export const useRestoreQueueItem = (trialId: string) => {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, expectedVersion }: QueueItemArgs) =>
      api.restoreQueueItem(trialId, itemId, expectedVersion),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['authoring-queue', trialId] });
    },
  });
};
